import { SmileOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { type MouseEvent, useState } from 'react';

import { Flex, VFlex } from '@/components/base';
import Empty from '@/components/Empty/Empty';
import List from '@/components/List/List';
import Typo from '@/components/Typography/Typo';
import BlockItem from '@/features/Feedback/components/BlockItem/BlockItem';
import useAppInit from '@/hooks/domain/App/useAppInit';
import useColor from '@/hooks/domain/useColor';
import useWorkspaceId from '@/hooks/domain/useWorkspaceId';
import service from '@/services/index';
import useDetailModalStore, {
  DETAIL_MODAL_TYPE,
} from '@/stores/pure/DetailModalStore';

const HomeNegativeFeedback = () => {
  const appInit = useAppInit();
  const color = useColor();
  const workspace_id = useWorkspaceId();
  const [selectedFeedback, setSelectedFeedback] =
    useState<SelectedFeedbackState>(null);
  const setNewModal = useDetailModalStore((state) => state.setNewModal);

  const { isFetching, data } = useQuery({
    queryKey: ['home-negative-feedbacks', workspace_id, appInit],
    queryFn: () => service.home.fetchMostNegativeFeedbacks(),
    enabled: appInit,
  });

  const onFeedbackClick = (
    e: MouseEvent,
    feedbackAlias: string,
    blockAlias: string,
  ) => {
    setSelectedFeedback({
      feedbackAlias,
      blockAlias,
    });
    setNewModal({
      type: DETAIL_MODAL_TYPE.FEEDBACK,
      idx: 0,
      alias: feedbackAlias,
      keys: [feedbackAlias, blockAlias],
      options: {
        focusedBlock: blockAlias,
      },
    });
  };

  return (
    <VFlex g={10} f={1}>
      <Flex j="space-between" a="center" style={{ height: 24 }}>
        <Typo
          type="label2"
          style={{
            fontWeight: 500,
          }}
          className="text-gray-700"
        >
          Recent 10 <span style={{ color: color.red600 }}>negative</span>{' '}
          feedbacks
        </Typo>
      </Flex>
      <VFlex style={{ height: 626 }}>
        <List
          className="flex-1 gap-1 border border-gray-300 rounded-2xl p-2"
          placeholder={{
            node: (
              <Empty
                icon={<SmileOutlined />}
                title="No negative feedback"
                description={`It appears that we haven't received any negative feedback so far.`}
              />
            ),
          }}
          isLoading={isFetching}
          data={data}
          render={(f) => {
            return f.blocks?.map((b) => {
              return (
                <BlockItem
                  key={b.alias}
                  date={f.date}
                  company={f.company_info?.name}
                  customer={f.customer_info?.name}
                  title={b.snippet}
                  onClick={(e) => onFeedbackClick(e, f.alias, b.alias)}
                  selected={selectedFeedback?.blockAlias === b.alias}
                />
              );
            });
          }}
        />
      </VFlex>
    </VFlex>
  );
};

export default HomeNegativeFeedback;
